import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { _className, ComponentSync, LL_H_C, LL_V_L } from '@nu-art/thunderstorm-frontend/index';
import { Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import './Renderer_SectionGraph.scss';
import { _keys, BadImplementationException, exists, formatTimestamp, isSameDay, sortArray } from '@nu-art/ts-common';
import { colors } from '@app/styles';
export class Renderer_SectionGraph extends ComponentSync {
    containerRef = React.createRef();
    //######################### Life Cycle #########################
    __onWindowResized() {
        this.setDimensions();
    }
    deriveStateFromProps(nextProps, state) {
        state.graphData = this.translateResultsToGraphData(nextProps.results);
        state.variableOrder = nextProps.variableOrder;
        state.activeNodeTimestamp = undefined;
        state.dimensions ??= {
            width: 0,
            height: 0,
        };
        return state;
    }
    componentDidMount() {
        this.setDimensions();
    }
    //######################### Logic #########################
    setDimensions = () => {
        const current = this.containerRef.current;
        if (!current)
            return;
        const rect = current.getBoundingClientRect();
        this.setState({
            dimensions: {
                width: rect.width,
                height: rect.height
            }
        });
    };
    translateResultsToGraphData(results) {
        const data = [];
        const varIds = _keys(results);
        varIds.forEach(varId => {
            const varResults = results[varId];
            varResults.forEach(result => {
                const existing = data.find(_r => _r.timestamp === result.timestamp);
                if (!existing) {
                    data.push({
                        timestamp: result.timestamp,
                        currentVisit: result.currentVisit,
                        [varId]: result.value,
                    });
                }
                else {
                    existing[varId] = result.value;
                }
            });
        });
        return sortArray(data, node => node.timestamp);
    }
    getNodeValue = (latestNode) => {
        const values = [];
        for (const varId of this.state.variableOrder) {
            const value = latestNode[varId];
            if (!exists(value))
                continue;
            values.push(value);
        }
        return values.join('/');
    };
    onActiveNodeChanged = (e) => {
        const activeNodeTimestamp = e.activeLabel;
        if (activeNodeTimestamp !== this.state.activeNodeTimestamp)
            this.setState({ activeNodeTimestamp });
    };
    getTimestampString = (timestamp) => {
        if (!timestamp)
            return;
        const date = new Date(timestamp);
        const today = new Date();
        return isSameDay(today, date)
            ? formatTimestamp('hh:mmA', timestamp)
            : formatTimestamp('D MMM YYYY · hh:mmA', timestamp);
    };
    getActiveNode = () => {
        if (!this.state.activeNodeTimestamp)
            return;
        return this.state.graphData.find(data => data.timestamp === this.state.activeNodeTimestamp);
    };
    getGraphData = () => {
        const data = this.state.graphData;
        const varIds = this.state.variableOrder;
        const filledData = data.slice(-5);
        while (filledData.length < 5) {
            const dummyData = { timestamp: 0, currentVisit: true };
            varIds.forEach(varId => {
                dummyData[varId] = null;
            });
            filledData.push(dummyData);
        }
        return filledData;
    };
    //######################### Render #########################
    render() {
        const renderGraph = this.state.graphData.length > 1;
        const className = _className('renderer__section-graph', renderGraph && 'with-graph');
        return _jsxs(LL_V_L, { className: className, children: [this.render_Title(), renderGraph && this.render_Graph()] });
    }
    render_Title = () => {
        const data = this.state.graphData;
        const latestNode = data[data.length - 1];
        const valueString = exists(latestNode) ? this.getNodeValue(latestNode) : 'N/A';
        const className = _className('renderer__section-graph__title', !latestNode?.currentVisit && 'fade');
        return _jsxs(LL_H_C, { className: className, children: [_jsx("div", { className: 'renderer__section-graph__title__value', children: valueString }), _jsx("div", { className: 'renderer__section-graph__title__timestamp', children: this.getTimestampString(latestNode?.timestamp) })] });
    };
    //######################### Render - Graph #########################
    render_Graph = () => {
        const varIds = this.state.variableOrder;
        const data = this.getGraphData();
        return _jsx("div", { ref: this.containerRef, className: 'renderer__section-graph__graph-wrapper', children: _jsxs(LineChart, { width: this.state.dimensions.width, height: this.state.dimensions.height, data: data, onMouseMove: this.onActiveNodeChanged, onMouseLeave: this.onActiveNodeChanged, children: [this.render_Graph_XAxis(), this.render_Graph_YAxis(), varIds.map(varId => {
                        return _jsx(Line, { dataKey: varId, stroke: colors.primary['primary-01'], dot: this.render_Graph_CustomDot, animationDuration: 0, connectNulls: true }, varId);
                    }), _jsx(Tooltip, { offset: 0, content: this.render_Graph_CustomTooltipContent })] }) });
    };
    render_Graph_XAxis = () => {
        return _jsx(XAxis, { dataKey: 'timestamp', tickFormatter: (value) => value === this.state.activeNodeTimestamp ? this.getTimestampString(value) ?? '' : '', interval: 0, stroke: colors.secondary['secondary-04'], tick: this.render_Graph_CustomTick });
    };
    render_Graph_YAxis = () => {
        //Rendering an invisible y-axis to force the graph to leave space at the top so that we have space for the tooltip value
        return _jsx(YAxis, { hide: true, domain: [0, (dataMax) => dataMax * 1.5] });
    };
    //######################### Render - Graph - Custom Entities #########################
    //Custom render for the dots on the graph line
    render_Graph_CustomDot = (props) => {
        if (!exists(props.value))
            return _jsx(React.Fragment, {}, props.key);
        const className = _className('renderer__section-graph__custom-dot', !props.payload?.currentVisit && 'fade');
        return _jsx("circle", { className: className, r: props.r, cx: props.cx, cy: props.cy }, props.key);
    };
    //Custom render for the timestamps on the xAxis
    render_Graph_CustomTick = (props) => {
        let textAnchor = 'middle';
        if (props.index === 0)
            textAnchor = 'start';
        else if (props.index === props.visibleTicksCount - 1)
            textAnchor = 'end';
        const value = props.payload.value;
        if (value === 0) //Timestamp 0 is the default for filled in dummy nodes
            return _jsx(_Fragment, {});
        const node = this.state.graphData.find(node => node.timestamp === value);
        if (!node) {
            this.logWarning(props);
            throw new BadImplementationException('Somehow rendering an X axis node with a timestamp that is not in the state data');
        }
        const textClassName = _className('renderer__section-graph__x-axis-node-text', !node?.currentVisit && 'fade');
        return _jsx("g", { transform: `translate(${props.x},${props.y})`, children: _jsx("text", { className: textClassName, x: 0, y: 0, dy: 14, textAnchor: textAnchor, children: props.tickFormatter(props.payload.value) }) });
    };
    //Custom render for the tooltip
    render_Graph_CustomTooltipContent = (props) => {
        const activeNode = this.getActiveNode();
        if (!activeNode)
            return;
        const valueString = this.getNodeValue(activeNode);
        const className = _className('renderer__section-graph__custom-tooltip', !activeNode?.currentVisit && 'fade');
        return _jsx("div", { className: className, style: {
                left: props.coordinate.x,
            }, children: valueString });
    };
}
//# sourceMappingURL=Renderer_SectionGraph.js.map